import React from 'react';
import { Link } from 'react-router-dom';
import Logo from '../components/Logo';
import * as styles from './TermsOfService.module.css';

const TermsOfService = () => {
  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <Logo linkTo="/" />
      </div>
      
      <div className={styles.content}>
        <h1>Terms of Service</h1>
        <p className={styles.lastUpdated}>Last updated: {new Date().toLocaleDateString()}</p>

        <section className={styles.section}>
          <h2>1. Acceptance of Terms</h2>
          <p>
            By accessing and using QR Butler ("Service", "Platform"), you accept and agree to be bound by the terms and provision of this agreement. 
            If you do not agree to abide by the above, please do not use this service.
          </p>
        </section>

        <section className={styles.section}>
          <h2>2. Description of Service</h2>
          <p>
            QR Butler is a digital ordering platform that enables restaurants to create digital menus accessible via QR codes, 
            process customer orders, and manage payments. Our service includes:
          </p>
          <ul>
            <li>QR code menu generation and management</li>
            <li>Digital menu creation and customization</li>
            <li>Order processing and payment collection</li>
            <li>Restaurant dashboard and analytics</li>
            <li>Customer ordering interface</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>3. User Accounts and Registration</h2>
          <h3>3.1 Restaurant Accounts</h3>
          <p>
            To use our service as a restaurant, you must register for an account and provide accurate, complete information. 
            You are responsible for safeguarding your account credentials and for all activities under your account.
          </p>
          
          <h3>3.2 Stripe Connect Integration</h3>
          <p>
            Restaurant accounts must connect to Stripe to process payments. You agree to comply with Stripe's terms of service 
            and acknowledge that payment processing is subject to Stripe's policies and procedures.
          </p>

          <h3>3.3 Customer Use</h3>
          <p>
            Customers may place orders without creating an account. By placing an order, customers agree to these terms 
            and provide accurate information for order fulfillment.
          </p>
        </section>

        <section className={styles.section}>
          <h2>4. Fees and Payment</h2>
          <h3>4.1 Platform Fees</h3>
          <p>
            QR Butler charges a 6% platform fee on each completed transaction. This fee is automatically deducted from 
            the payment before transfer to the restaurant's connected Stripe account.
          </p>

          <h3>4.2 Payment Processing</h3>
          <p>
            All payments are processed through Stripe. Payment processing fees charged by Stripe are separate from our platform fee. 
            Refunds and chargebacks are handled according to Stripe's policies.
          </p>

          <h3>4.3 Fee Changes</h3>
          <p>
            We reserve the right to modify our fee structure with 30 days written notice to registered restaurant users.
          </p>
        </section>

        <section className={styles.section}>
          <h2>5. Restaurant Responsibilities</h2>
          <p>As a restaurant using our platform, you agree to:</p>
          <ul>
            <li>Provide accurate menu information, pricing, and availability</li>
            <li>Fulfill orders in a timely manner according to your stated preparation times</li>
            <li>Maintain food safety and quality standards</li>
            <li>Comply with all applicable local, state, and federal regulations</li>
            <li>Handle customer service issues professionally</li>
            <li>Update menu items and availability as needed</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>6. Customer Responsibilities</h2>
          <p>As a customer using our platform, you agree to:</p>
          <ul>
            <li>Provide accurate contact and payment information</li>
            <li>Be available to receive your order at the designated location</li>
            <li>Comply with restaurant policies and local laws</li>
            <li>Not abuse or misuse the ordering system</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>7. Prohibited Uses</h2>
          <p>You may not use our service:</p>
          <ul>
            <li>For any unlawful purpose or to solicit others to perform unlawful acts</li>
            <li>To violate any international, federal, provincial, or state regulations, rules, laws, or local ordinances</li>
            <li>To infringe upon or violate our intellectual property rights or the intellectual property rights of others</li>
            <li>To harass, abuse, insult, harm, defame, slander, disparage, intimidate, or discriminate</li>
            <li>To submit false or misleading information</li>
            <li>To upload or transmit viruses or any other type of malicious code</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>8. Intellectual Property Rights</h2>
          <p>
            The service and its original content, features, and functionality are and will remain the exclusive property of 
            QR Butler and its licensors. The service is protected by copyright, trademark, and other laws.
          </p>
        </section>

        <section className={styles.section}>
          <h2>9. Privacy Policy</h2>
          <p>
            Your privacy is important to us. Our Privacy Policy explains how we collect, use, and protect your information 
            when you use our service. By using our service, you agree to the collection and use of information in accordance 
            with our Privacy Policy.
          </p>
        </section>

        <section className={styles.section}>
          <h2>10. Service Availability</h2>
          <p>
            We strive to maintain high service availability but cannot guarantee uninterrupted service. We reserve the right to:
          </p>
          <ul>
            <li>Modify or discontinue the service with reasonable notice</li>
            <li>Refuse service to anyone for any reason at any time</li>
            <li>Suspend accounts that violate these terms</li>
          </ul>
        </section>

        <section className={styles.section}>
          <h2>11. Limitation of Liability</h2>
          <p>
            QR Butler shall not be liable for any indirect, incidental, special, consequential, or punitive damages, 
            including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from 
            your use of the service.
          </p>
        </section>

        <section className={styles.section}>
          <h2>12. Indemnification</h2>
          <p>
            You agree to defend, indemnify, and hold harmless QR Butler from and against any claims, damages, obligations, 
            losses, liabilities, costs, and expenses (including but not limited to attorney's fees) arising from your use of 
            the service or violation of these terms.
          </p>
        </section>

        <section className={styles.section}>
          <h2>13. Termination</h2>
          <p>
            We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, 
            including without limitation if you breach the Terms. Upon termination, your right to use the service will cease immediately.
          </p>
        </section>

        <section className={styles.section}>
          <h2>14. Governing Law</h2>
          <p>
            These Terms shall be interpreted and governed by the laws of [Your State/Province], without regard to its conflict 
            of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
          </p>
        </section>

        <section className={styles.section}>
          <h2>15. Changes to Terms</h2>
          <p>
            We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, 
            we will try to provide at least 30 days notice prior to any new terms taking effect.
          </p>
        </section>

        <section className={styles.section}>
          <h2>16. Contact Information</h2>
          <p>
            If you have any questions about these Terms of Service, please contact us at:
          </p>
          <div className={styles.contactInfo}>
            <p><strong>Email:</strong> support@qrbutler.com</p>
            <p><strong>Address:</strong> [Your Business Address]</p>
          </div>
        </section>

        <div className={styles.footer}>
          <Link to="/" className={styles.backButton}>← Back to Home</Link>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;